import React, { useEffect, useRef, useState } from "react";
import { Button, Input, Tag, message, Space, Modal, Card, Dropdown } from "antd";
import {
  PlusOutlined,
  ExportOutlined,
  ImportOutlined,
  FileProtectOutlined,
  BoldOutlined,
  ItalicOutlined,
  CodeOutlined,
  OrderedListOutlined,
  UnorderedListOutlined,
  LinkOutlined,
  PictureOutlined,
  TableOutlined,
  FunctionOutlined
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { createPost, exportAllPosts, importPosts } from "../../api/mockData";

import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { TextStyle } from "@tiptap/extension-text-style";
import Link from "@tiptap/extension-link";
import Image from "@tiptap/extension-image";
import { Table, TableRow, TableHeader, TableCell } from "@tiptap/extension-table";

// 导入KaTeX用于LaTeX渲染
import katex from 'katex';
import 'katex/dist/katex.min.css';

// 简单有效的LaTeX扩展 - 使用直接DOM操作
const LatexExtension = {
  name: 'latex',
  
  addProseMirrorPlugins() {
    return [
      {
        view: (view) => {
          let timeoutId = null;
          
          const renderLatex = () => {
            const editorElement = view.dom;
            const textNodes = editorElement.querySelectorAll('p, h1, h2, h3, h4, h5, h6, li, div');
            
            textNodes.forEach(node => {
              const originalHTML = node.innerHTML;
              
              // 处理行内公式 $...$
              let newHTML = originalHTML.replace(/\$([^$]+)\$/g, (match, latexContent) => {
                try {
                  return katex.renderToString(latexContent, {
                    displayMode: false,
                    throwOnError: false
                  });
                } catch (error) {
                  console.warn('LaTeX渲染错误:', error);
                  return match; // 渲染失败时返回原文本
                }
              });
              
              // 处理块级公式 $$...$$
              newHTML = newHTML.replace(/\$\$([^$]+)\$\$/g, (match, latexContent) => {
                try {
                  return katex.renderToString(latexContent, {
                    displayMode: true,
                    throwOnError: false
                  });
                } catch (error) {
                  console.warn('LaTeX渲染错误:', error);
                  return match; // 渲染失败时返回原文本
                }
              });
              
              if (newHTML !== originalHTML) {
                node.innerHTML = newHTML;
              }
            });
          };
          
// 初始渲染
          setTimeout(renderLatex, 100);
          
          // 监听编辑器变化
          const handleUpdate = () => {
            if (timeoutId) {
              clearTimeout(timeoutId);
            }
            timeoutId = setTimeout(renderLatex, 300); // 防抖处理
          };
          
          view.dom.addEventListener('input', handleUpdate);
          view.dom.addEventListener('change', handleUpdate);
          
          return {
            destroy: () => {
              if (timeoutId) {
                clearTimeout(timeoutId);
              }
              view.dom.removeEventListener('input', handleUpdate);
              view.dom.removeEventListener('change', handleUpdate);
            }
          };
        }
      }
    ];
  }
};

export default function WritePost() {
  const navigate = useNavigate();

  const [title, setTitle] = useState("");
  const [summary, setSummary] = useState("");
  const [content, setContent] = useState("");
  const [tags, setTags] = useState([]);

  const [inputVisible, setInputVisible] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const inputRef = useRef(null);

  const [loading, setLoading] = useState(false);
  const fileInputRef = useRef(null);

// ✅ 关键：用 ref 保存 editor 实例，避免初始化时 editor 还是 null
  const editorRef = useRef(null);

  // ✅ 注入必要的编辑器 CSS（包含LaTeX样式）
  useEffect(() => {
    const style = document.createElement("style");
    style.setAttribute("data-tiptap-style", "1");
    style.innerHTML = `
      /* 强制所有元素使用主题色 */
      .write-post-container * {
        color: var(--theme-text) !important;
        background-color: var(--theme-card-bg) !important;
      }
      
      .tiptap-editor .ProseMirror {
        min-height: 400px;
        outline: none;
        white-space: pre-wrap;
        word-break: break-word;
        background: var(--theme-card-bg) !important;
        color: var(--theme-text) !important;
        border: 1px solid var(--theme-card-border) !important;
        border-radius: 4px;
        padding: 12px;
      }
      
      /* KaTeX样式适配主题色 */
      .tiptap-editor .ProseMirror .katex {
        color: var(--theme-text) !important;
      }
      
      .tiptap-editor .ProseMirror .katex .katex-html {
        color: var(--theme-text) !important;
      }
      
      .tiptap-editor .ProseMirror .katex-rendered {
        display: inline-block;
        vertical-align: middle;
      }
      
      /* LaTeX公式样式 */
      .tiptap-editor .ProseMirror .latex-inline {
        display: inline-block;
        vertical-align: middle;
        background: rgba(var(--theme-primary-rgb), 0.1);
        padding: 2px 6px;
        border-radius: 4px;
        margin: 0 2px;
        border: 1px solid rgba(var(--theme-primary-rgb), 0.3);
      }
      
      .tiptap-editor .ProseMirror .latex-block {
        display: block;
        text-align: center;
        background: rgba(var(--theme-primary-rgb), 0.05);
        padding: 12px;
        border-radius: 8px;
        margin: 16px 0;
        border: 1px solid rgba(var(--theme-primary-rgb), 0.2);
      }
      
      .tiptap-editor .ProseMirror p { margin: 0 0 12px; color: var(--theme-text) !important; }
      .tiptap-editor .ProseMirror h2 { 
        margin: 16px 0 12px; 
        font-size: 20px; 
        color: var(--theme-text) !important;
      }
      .tiptap-editor .ProseMirror blockquote {
        border-left: 4px solid var(--theme-primary) !important;
        padding-left: 12px;
        margin: 12px 0;
        color: var(--theme-text) !important;
        opacity: 0.8;
        background-color: rgba(var(--theme-primary-rgb), 0.05) !important;
      }
      .tiptap-editor .ProseMirror code {
        font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
        background: rgba(var(--theme-primary-rgb), 0.1) !important;
        padding: 2px 6px;
        border-radius: 4px;
        color: var(--theme-text) !important;
      }
      .tiptap-editor .ProseMirror pre {
        background: var(--theme-card-bg) !important;
        color: var(--theme-text) !important;
        padding: 12px 14px;
        border-radius: 8px;
        overflow-x: auto;
        margin: 12px 0;
        border: 1px solid var(--theme-card-border) !important;
      }
      .tiptap-editor .ProseMirror pre code {
        background: transparent !important;
        padding: 0;
        color: inherit !important;
      }
      .tiptap-editor .ProseMirror table {
        border-collapse: collapse;
        table-layout: fixed;
        width: 100%;
        margin: 12px 0;
      }
      .tiptap-editor .ProseMirror td,
      .tiptap-editor .ProseMirror th {
        border: 1px solid var(--theme-card-border) !important;
        padding: 8px;
        vertical-align: top;
        color: var(--theme-text) !important;
        background-color: var(--theme-card-bg) !important;
      }
      .tiptap-editor .ProseMirror th {
        background-color: rgba(var(--theme-primary-rgb), 0.1) !important;
        font-weight: 600;
      }
      .tiptap-editor .ProseMirror img {
        max-width: 100% !important;
        height: auto !important;
        width: auto !important;
        border-radius: 6px;
        margin: 10px 0;
        display: block;
        /* 新增样式确保图片不超出边界 */
        box-sizing: border-box;
        object-fit: contain;
        max-height: 400px; /* 限制最大高度 */
        overflow: hidden;
      }
      
      /* 确保编辑器容器也有正确的边界控制 */
      .tiptap-editor .ProseMirror {
        max-width: 100%;
        overflow: hidden;
        box-sizing: border-box;
        word-wrap: break-word;
        overflow-wrap: break-word;
        overflow-x: hidden;
      }
      
      /* 编辑器工具栏主题色 */
      .tiptap-editor .editor-toolbar {
        background: var(--theme-card-bg) !important;
        border: 1px solid var(--theme-card-border) !important;
        border-radius: 4px 4px 0 0;
        padding: 8px;
      }
      
      .tiptap-editor .editor-toolbar button {
        background: var(--theme-card-bg) !important;
        border: 1px solid var(--theme-card-border) !important;
        color: var(--theme-text) !important;
      }
      
      .tiptap-editor .editor-toolbar button:hover {
        background: var(--theme-primary) !important;
        color: white !important;
      }
      
      /* 输入框和按钮主题色 */
      .write-post-container .ant-input,
      .write-post-container .ant-input-textarea {
        background: var(--theme-card-bg) !important;
        border: 1px solid var(--theme-card-border) !important;
        color: var(--theme-text) !important;
      }
      
      .write-post-container .ant-input:focus,
      .write-post-container .ant-input-textarea:focus {
        background: var(--theme-card-bg) !important;
        border-color: var(--theme-primary) !important;
        box-shadow: 0 0 0 2px rgba(var(--theme-primary-rgb), 0.2) !important;
        color: var(--theme-text) !important;
      }
      
      .write-post-container .ant-btn {
        background: var(--theme-card-bg) !important;
        border: 1px solid var(--theme-card-border) !important;
        color: var(--theme-text) !important;
      }
      
      .write-post-container .ant-btn:hover {
        background: rgba(var(--theme-primary-rgb), 0.1) !important;
        border-color: var(--theme-primary) !important;
        color: var(--theme-text) !important;
      }
      
      .write-post-container .ant-btn-primary {
        background: linear-gradient(135deg, var(--theme-primary) 0%, var(--theme-secondary) 100%) !important;
border-color: var(--theme-primary) !important;
        color: white !important;
      }
      
      .write-post-container .ant-btn-primary:hover {
        background: linear-gradient(135deg, var(--theme-secondary) 0%, var(--theme-accent) 100%) !important;
        border-color: var(--theme-secondary) !important;
      }
      
      /* 标签主题色 */
      .write-post-container .ant-tag {
        background: var(--theme-card-bg) !important;
        border: 1px solid var(--theme-card-border) !important;
        color: var(--theme-text) !important;
      }
      
      .write-post-container .ant-tag:hover {
        background: rgba(var(--theme-primary-rgb), 0.1) !important;
        border-color: var(--theme-primary) !important;
      }
      
      /* 卡片主题色 */
      .write-post-container .ant-card {
        background: var(--theme-card-bg) !important;
        border: 1px solid var(--theme-card-border) !important;
        color: var(--theme-text) !important;
      }
      
      .write-post-container .ant-card .ant-card-head {
        background: var(--theme-card-bg) !important;
        border-bottom: 1px solid var(--theme-card-border) !important;
        color: var(--theme-text) !important;
      }
      
      .write-post-container .ant-card .ant-card-body {
        background: var(--theme-card-bg) !important;
        color: var(--theme-text) !important;
      }
      
      /* 下拉菜单主题色 */
      .write-post-container .ant-dropdown-menu {
        background: var(--theme-card-bg) !important;
        border: 1px solid var(--theme-card-border) !important;
        color: var(--theme-text) !important;
      }
      
      .write-post-container .ant-dropdown-menu-item {
        background: var(--theme-card-bg) !important;
        color: var(--theme-text) !important;
      }
      
      .write-post-container .ant-dropdown-menu-item:hover {
        background: rgba(var(--theme-primary-rgb), 0.1) !important;
        color: var(--theme-text) !important;
      }
      
      /* 提示文字主题色 */
      .write-post-container .hint-text {
        color: var(--theme-text) !important;
        opacity: 0.7;
      }
    `;
    document.head.appendChild(style);
    return () => {
      const old = document.querySelector('style[data-tiptap-style="1"]');
      if (old) old.remove();
    };
  }, []);
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        link: false,
        codeBlock: true,
      }),
      TextStyle,
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          target: "_blank",
          rel: "noopener noreferrer",
        },
      }),
      Image.configure({
        inline: false,
        allowBase64: true,
      }),
      Table.configure({ resizable: true }),
      TableRow,
      TableHeader,
      TableCell,
      LatexExtension, // 使用新的LaTeX扩展
    ],
    content: content || "",
    onCreate: ({ editor }) => {
      editorRef.current = editor;
    },
    onUpdate: ({ editor }) => {
      setContent(editor.getHTML());
    },
    editorProps: {
      attributes: {
        class: "tiptap-editor-prosemirror",
      },

      // ✅ 粘贴图片（截图/剪贴板图片文件）
      handlePaste: (view, event) => {
        const e = event; // 原生 ClipboardEvent
        const items = Array.from(e.clipboardData?.items || []);
        const imgItem = items.find((i) => i.type && i.type.startsWith("image/"));
        if (!imgItem) return false;

        const file = imgItem.getAsFile();
        if (!file) return false;

        e.preventDefault(); // ✅ 必须阻止默认粘贴

        const reader = new FileReader();
        reader.onload = () => {
          const src = String(reader.result || "");
          editorRef.current?.chain().focus().setImage({ src }).run();
        };
        reader.readAsDataURL(file);

        return true;
      },

      // ✅ 拖拽图片进来
      handleDrop: (view, event) => {
        const e = event;
        const files = Array.from(e.dataTransfer?.files || []);
        const img = files.find((f) => f.type && f.type.startsWith("image/"));
        if (!img) return false;

        e.preventDefault();

        const reader = new FileReader();
        reader.onload = () => {
          const src = String(reader.result || "");
          editorRef.current?.chain().focus().setImage({ src }).run();
        };
        reader.readAsDataURL(img);

        return true;
      },
    },
  });

  // ====== 文章操作 ======
  const handleSubmit = async () => {
    if (!title.trim()) return message.warning("请输入标题");
    setLoading(true);
    try {
      const result = await createPost({ title, summary, content, tags });
      if (result.success) {
        message.success("文章创建成功！");
        navigate("/");
      } else {
        message.error("发布失败：" + result.message);
      }
    } catch (err) {
      message.error("发布失败：" + (err?.message || "未知错误"));
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      const result = await exportAllPosts();
      result.success ? message.success(result.message) : message.error(result.message);
} catch {
      message.error("导出失败");
    }
  };
const handleImport = () => fileInputRef.current?.click();

  const handleFileSelect = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      const result = await importPosts(file);
      if (result.success) {
        message.success(result.message);
        Modal.info({
          title: "导入成功",
          content: (
            <div>
              <p>成功导入 {result.posts.length} 篇文章：</p>
              <ul>
                {result.posts.map((post, index) => (
                  <li key={index}>{post.title}</li>
                ))}
              </ul>
              <p>文章已添加到系统中，请刷新页面查看。</p>
            </div>
          ),
          onOk() {
            window.location.reload();
          },
        });
      } else {
        message.error(result.message);
      }
    } catch (err) {
      message.error("导入失败：" + (err?.message || "未知错误"));
    } finally {
      event.target.value = "";
    }
  };

  // ====== 标签 ======
  const handleClose = (removedTag) => setTags(tags.filter((t) => t !== removedTag));

  const showInput = () => {
    setInputVisible(true);
    setTimeout(() => inputRef.current?.focus(), 80);
  };

  const handleInputConfirm = () => {
    const v = inputValue.trim();
    if (v && !tags.includes(v)) setTags([...tags, v]);
    setInputVisible(false);
    setInputValue("");
  };

  // ====== TipTap 工具栏 ======
  const cmd = () => editorRef.current?.chain().focus();
  const handleBold = () => cmd()?.toggleBold().run();
  const handleItalic = () => cmd()?.toggleItalic().run();
  // ✅ 代码块：确保是 codeBlock，不是 inline code
  const handleCodeBlock = () => cmd()?.toggleCodeBlock().run();

  // 添加LaTeX公式处理函数
  const handleInlineLatex = () => {
    const formula = prompt("请输入行内 LaTeX 公式:", "E = mc^2");
    if (formula) {
      editorRef.current?.commands.insertContent(`$${formula}$`);
    }
  };

  const handleBlockLatex = () => {
    const formula = prompt("请输入块级 LaTeX 公式:", "\\int_{-\\infty}^{\\infty} e^{-x^2} dx = \\sqrt{\\pi}");
    if (formula) {
      editorRef.current?.commands.insertContent(`$$${formula}$$`);
    }
  };

  const handleLink = () => {
    const url = window.prompt("请输入链接地址:");
    if (!url) return;
    cmd()?.extendMarkRange("link").setLink({ href: url }).run();
  };

  const handleImage = () => {
    const url = window.prompt("请输入图片链接(https 或 data:image/...):");
    if (!url) return;
    cmd()?.setImage({ src: url }).run();
  };

  const handleBulletList = () => cmd()?.toggleBulletList().run();
  const handleOrderedList = () => cmd()?.toggleOrderedList().run();
  const handleBlockquote = () => cmd()?.toggleBlockquote().run();

  const handleTable = () => {
    const rows = parseInt(prompt("行数:", "3"), 10) || 3;
    const cols = parseInt(prompt("列数:", "3"), 10) || 3;
    cmd()?.insertTable({ rows, cols, withHeaderRow: true }).run();
  };

  const handleHeading = (level) => cmd()?.toggleHeading({ level }).run();
  const handleUndo = () => cmd()?.undo().run();
  const handleRedo = () => cmd()?.redo().run();

  return (
    <div className="write-post-container">
      <input
        type="file"
        ref={fileInputRef}
        style={{ display: "none" }}
        accept=".json"
        onChange={handleFileSelect}
      />

      <div style={{ marginBottom: 16, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div className="page-title">写文章</div>
        <Space>
          <Button icon={<FileProtectOutlined />} disabled type="default">
            已启用文件存储
          </Button>
          <Button icon={<ExportOutlined />} onClick={handleExport}>
            导出所有文章
          </Button>
          <Button icon={<ImportOutlined />} onClick={handleImport}>
            导入文章
          </Button>
        </Space>
      </div>

      <div style={{ marginBottom: 24, fontSize: 20, color: "var(--theme-text)" }}>标题</div>
      <div style={{ marginBottom: 16 }}>
        <Input
          placeholder="无题？满堂花醉三千客，一剑霜寒十四州..."
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          size="large"
        />
      </div>

      <div style={{ marginBottom: 24, fontSize: 20, color: "var(--theme-text)" }}>摘要</div>
      <div style={{ marginBottom: 16 }}>
        <Input.TextArea
          placeholder="做个总结吧：水满井时井亦镜，缘散尽时情亦清..."
          value={summary}
          onChange={(e) => setSummary(e.target.value)}
          rows={3}
        />
      </div>

      <div style={{ marginBottom: 16 }}>
        <div style={{ marginBottom: 24, fontSize: 20, color: "var(--theme-text)" }}>标签：</div>
        <div>
          {tags.map((tag) => (
            <Tag key={tag} closable onClose={() => handleClose(tag)}>
              {tag}
            </Tag>
          ))}
          {inputVisible ? (
            <Input
              ref={inputRef}
              type="text"
              size="small"
              style={{ width: 120 }}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onBlur={handleInputConfirm}
              onPressEnter={handleInputConfirm}
            />
          ) : (
            <Tag onClick={showInput} style={{ borderStyle: "dashed" }}>
              <PlusOutlined /> 新标签
            </Tag>
          )}
        </div>
      </div>

      <div style={{ marginBottom: 24 }}>
        <div style={{ marginBottom: 16, fontSize: 20, color: "var(--theme-text)" }}>内容</div>

        <Card
          title={
            <div style={{ display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
              <span style={{ color: "var(--theme-text)" }}>编辑器</span>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <Button
                  size="small"
                  icon={<BoldOutlined />}
                  onClick={handleBold}
                  type={editorRef.current?.isActive("bold") ? "primary" : "text"}
                >
                  加粗
                </Button>

                <Button
                  size="small"
                  icon={<ItalicOutlined />}
                  onClick={handleItalic}
                  type={editorRef.current?.isActive("italic") ? "primary" : "text"}
                >
                  斜体
                </Button>

                <Button
                  size="small"
                  icon={<CodeOutlined />}
                  onClick={handleCodeBlock}
                  type={editorRef.current?.isActive("codeBlock") ? "primary" : "text"}
                >
                  代码块
                </Button>

                <Button size="small" icon={<LinkOutlined />} onClick={handleLink} type="text">
                  链接
                </Button>

                <Button size="small" icon={<PictureOutlined />} onClick={handleImage} type="text">
                  图片
                </Button>

                <Button
                  size="small"
                  icon={<UnorderedListOutlined />}
                  onClick={handleBulletList}
                  type={editorRef.current?.isActive("bulletList") ? "primary" : "text"}
                >
                  列表
                </Button>

                <Button
                  size="small"
                  icon={<OrderedListOutlined />}
                  onClick={handleOrderedList}
                  type={editorRef.current?.isActive("orderedList") ? "primary" : "text"}
                >
                  有序
                </Button>

                <Button
                  size="small"
                  onClick={handleBlockquote}
                  type={editorRef.current?.isActive("blockquote") ? "primary" : "text"}
                >
                  引用
                </Button>

                {/* 标题下拉菜单 */}
                <Dropdown
                  menu={{
                    items: [
                      {
                        key: '1',
                        label: '标题1',
                        onClick: () => handleHeading(1),
                      },
                      {
                        key: '2',
                        label: '标题2',
                        onClick: () => handleHeading(2),
                      },
                      {
                        key: '3',
                        label: '标题3',
                        onClick: () => handleHeading(3),
                      },
                      {
                        key: '4',
                        label: '标题4',
                        onClick: () => handleHeading(4),
                      },
                      {
                        key: '5',
                        label: '标题5',
                        onClick: () => handleHeading(5),
                      },
                    ],
                  }}
                >
                  <Button size="small" type="text">标题</Button>
                </Dropdown>

                <Button
                  size="small"
                  icon={<TableOutlined />}
                  onClick={handleTable}
                  type="text"
                >
                  表格
                </Button>

                {/* 添加LaTeX公式按钮 */}
                <Button
                  size="small"
                  icon={<FunctionOutlined />}
                  onClick={handleInlineLatex}
                  type="text"
                >
                  行内LaTeX
                </Button>

                <Button
                  size="small"
                  icon={<FunctionOutlined />}
                  onClick={handleBlockLatex}
                  type="text"
                >
                  块级LaTeX
                </Button>

                <Button size="small" onClick={handleUndo} type="text">
                  撤销
                </Button>
                <Button size="small" onClick={handleRedo} type="text">
                  重做
                </Button>
              </div>
            </div>
          }
          style={{ width: "100%" }}
          bodyStyle={{ padding: 0 }}
        >
          <div className="tiptap-editor">
            <EditorContent
              editor={editor}
              style={{
                minHeight: 400,
                borderRadius: 6,
                padding: 16,
                background: "var(--theme-card-bg)",
                color: "var(--theme-text)"
              }}
            />
          </div>
        </Card>

        <div style={{ marginTop: 8, fontSize: 12, color: "var(--theme-text)", opacity: 0.7 }}>
          提示：支持粘贴截图/图片（Ctrl+V）与拖拽图片到编辑器。
        </div>
      </div>

      <div style={{ display: "flex", gap: 12 }}>
        <Button 
          type="default" 
          loading={loading} 
          onClick={handleSubmit}
        >
          保存文章
        </Button>
        <Button onClick={() => navigate("/")}>取消</Button>
      </div>
    </div>
  );
}